import type { PlacesContainer } from '@elara/client-core/containers/PlacesContainer';
import type { Place } from '@elara/client-core/models/PlaceModel';
import type { ItineraryItemType } from '@elara/domain/models/schemas/itinerary-item-schemas';
import type { PlaceOpeningHours } from '@elara/domain/models/schemas/place-schemas';

import type { DateRangeValue } from './DateRangeField.js';

import React, { useMemo, useState } from 'react';

import { parseDocumentId } from '@elara/client-core/lib/utils';

import { useRefreshPlace } from '#lib/use-refresh-place.js';
import { useApp, useToastState } from '#states/hooks.js';

import { Button } from './button.js';
import { DateRangeField } from './DateRangeField.js';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog.js';

export interface ItineraryDatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemType: ItineraryItemType;
  referenceId?: string;
  title: string;
  initialStartDateTime?: string;
  initialEndDateTime?: string;
  tripStartDate?: string;
  tripEndDate?: string;
  onConfirm: (dates: { startDateTime: string; endDateTime?: string }) => Promise<void>;
  confirmLabel?: string;
}

function ActivityDatesDialog({
  open,
  onOpenChange,
  itemType,
  referenceId,
  initialStartDateTime,
  initialEndDateTime,
  tripStartDate,
  tripEndDate,
  onConfirm,
  confirmLabel = 'Add to itinerary',
}: ItineraryDatesDialogProps) {
  const { features } = useApp('features');
  const { places } = features as { places: PlacesContainer };
  const toastState = useToastState();
  const [dateValue, setDateValue] = useState<DateRangeValue | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch place for opening hours (place items only)
  const placeIdForRefresh =
    open && itemType === 'place' && referenceId ? (parseDocumentId(referenceId) ?? referenceId) : undefined;
  useRefreshPlace(placeIdForRefresh);

  const placeHandle = useMemo(() => {
    if (!placeIdForRefresh) return undefined;
    return places.ClientPlace.get({ id: placeIdForRefresh });
  }, [placeIdForRefresh, places]);

  const place = placeHandle?.data as Place | undefined;
  const placeOpeningHours: PlaceOpeningHours | null | undefined =
    itemType === 'place' ? (place?.openingHours ?? null) : null;

  const handleConfirm = async () => {
    if (!dateValue?.isValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onConfirm({ startDateTime: dateValue.startDateTime, endDateTime: dateValue.endDateTime });
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('[ActivityDatesDialog] Failed to confirm dates', error);
      toastState.addToast({ title: 'Failed to save dates', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="@container">
        <DialogHeader>
          <DialogTitle className="mb-1">When do you want to go?</DialogTitle>
        </DialogHeader>

        <DateRangeField
          key={open ? `open-${initialStartDateTime ?? ''}-${initialEndDateTime ?? ''}` : 'closed'}
          initialStartDateTime={initialStartDateTime}
          initialEndDateTime={initialEndDateTime}
          tripStartDate={tripStartDate}
          tripEndDate={tripEndDate}
          placeOpeningHours={placeOpeningHours}
          isOpenNow={place?.isOpenNow}
          onChange={setDateValue}
        />

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!dateValue?.isValid || isSubmitting} isBusy={isSubmitting}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ItineraryDatesDialog(props: ItineraryDatesDialogProps) {
  return <ActivityDatesDialog {...props} />;
}
